import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ServerStatus.css';

const API = import.meta.env.VITE_API_URL;
const MIN_COL_W = 70;
const MAX_COL_W = 720;
const SESSION_RESIZE_HANDLE_W = 12;
const SESSION_COL_WIDTHS_KEY = 'server-status-session-col-widths.v1';
const DEFAULT_SESSION_COL_WIDTHS = {
  user: 180,
  login: 140,
  type: 100,
  client: 220,
  ip: 140,
  useragent: 280,
  issued_at: 180,
  last_seen_at: 180,
  expires_at: 180,
  requests: 100,
  actions: 140,
};

const loadStoredSessionColWidths = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return DEFAULT_SESSION_COL_WIDTHS;
    const raw = window.localStorage.getItem(SESSION_COL_WIDTHS_KEY);
    if (!raw) return DEFAULT_SESSION_COL_WIDTHS;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_SESSION_COL_WIDTHS;
    return { ...DEFAULT_SESSION_COL_WIDTHS, ...parsed };
  } catch {
    return DEFAULT_SESSION_COL_WIDTHS;
  }
};

const clampSessionColWidth = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return MIN_COL_W;
  return Math.min(MAX_COL_W, Math.max(MIN_COL_W, Math.round(n)));
};

export default function ServerStatus() {
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState({ data: [], meta: { total: 0, limit: 50, offset: 0 } });
  const [loading, setLoading] = useState(true);
  const [sessionSort, setSessionSort] = useState({ field: 'last_seen_at', direction: 'desc' });
  const [sessionColWidths, setSessionColWidths] = useState(loadStoredSessionColWidths);
  const sessionResizeRef = useRef(null);
  const sessionSkipClickRef = useRef(false);
  const sessionHeaderRowRef = useRef(null);

  const clearSessionResizeHover = () => {
    const row = sessionHeaderRowRef.current;
    if (!row) return;
    row.querySelectorAll('.server-status-resize-active').forEach((el) => {
      el.classList.remove('server-status-resize-active');
    });
  };

  const load = async (offset = 0) => {
    setLoading(true);
    try {
      const [h, s, a] = await Promise.all([
        fetch(`${API}/api/admin/server/health`).then(r => r.json()).catch(()=>null),
        fetch(`${API}/api/admin/sessions/stats`).then(r => r.json()).catch(()=>null),
        fetch(`${API}/api/admin/sessions/active?limit=50&offset=${offset}`).then(r => r.json()).catch(()=>({ data: [], meta: { total: 0, limit: 50, offset } })),
      ]);
      if (h) setHealth(h);
      if (s) setStats(s);
      if (a) setSessions(a);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(0); }, []);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(SESSION_COL_WIDTHS_KEY, JSON.stringify(sessionColWidths));
      }
    } catch {}
  }, [sessionColWidths]);

  useEffect(() => {
    const onMove = (e) => {
      const ctx = sessionResizeRef.current;
      if (!ctx) return;
      const next = clampSessionColWidth(ctx.startWidth + (e.clientX - ctx.startX));
      setSessionColWidths((prev) => {
        if (prev[ctx.key] === next) return prev;
        return { ...prev, [ctx.key]: next };
      });
    };
    const onUp = () => {
      if (!sessionResizeRef.current) return;
      sessionResizeRef.current = null;
      try { document.body.classList.remove('col-resizing'); } catch {}
      try { document.body.style.cursor = ''; } catch {}
      window.setTimeout(() => { sessionSkipClickRef.current = false; }, 0);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try { document.body.classList.remove('col-resizing'); } catch {}
      try { document.body.style.cursor = ''; } catch {}
    };
  }, []);

  const invalidate = async (jti) => {
    if (!jti) return;
    try {
      await fetch(`${API}/api/admin/sessions/${jti}/invalidate`, { method: 'POST' });
      load(sessions.meta.offset);
    } catch {}
  };

  const fmt = (d) => d ? new Date(d).toLocaleString() : '';
  const fmtUptime = (s) => {
    const n = Math.max(0, Math.floor(Number(s || 0)));
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const sec = n % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  const getSessionSortValue = (row, field) => {
    switch (field) {
      case 'user':
        return String(row.userFullName || row.login || row.userid || '').toLowerCase();
      case 'login':
        return String(row.login || '').toLowerCase();
      case 'type':
        return (row.isclient === true || row.isclient === 1 || String(row.isclient).toLowerCase() === 'true' || String(row.isclient) === '1') ? 'client' : 'internal';
      case 'client':
        return String(row.clientnames || '').toLowerCase();
      case 'ip':
        return String(row.ip || '').toLowerCase();
      case 'useragent':
        return String(row.useragent || '').toLowerCase();
      case 'issued_at':
      case 'last_seen_at':
      case 'expires_at':
        return new Date(row[field] || 0).getTime();
      case 'requests':
        return Number(row.requests || 0);
      default:
        return String(row[field] || '').toLowerCase();
    }
  };

  const sortedSessions = useMemo(() => {
    const rows = Array.isArray(sessions?.data) ? [...sessions.data] : [];
    const dir = sessionSort.direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = getSessionSortValue(a, sessionSort.field);
      const bv = getSessionSortValue(b, sessionSort.field);
      if (av == null && bv == null) return 0;
      if (av == null) return 1 * dir;
      if (bv == null) return -1 * dir;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return rows;
  }, [sessions?.data, sessionSort]);

  const toggleSessionSort = (field) => {
    setSessionSort((prev) => (
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' }
    ));
  };

  const renderSortIndicator = (field) => {
    if (sessionSort.field !== field) return '↕';
    return sessionSort.direction === 'asc' ? '▲' : '▼';
  };

  const startSessionResize = (key, e, thOverride = null) => {
    e.preventDefault();
    e.stopPropagation();
    const th = thOverride || e.currentTarget.closest('th');
    sessionSkipClickRef.current = true;
    sessionResizeRef.current = {
      key,
      startX: e.clientX,
      startWidth: th?.getBoundingClientRect().width ?? Number(sessionColWidths[key] || DEFAULT_SESSION_COL_WIDTHS[key] || 140),
    };
    try { document.body.classList.add('col-resizing'); } catch {}
  };

  const isInSessionResizeLane = (event) => {
    const th = event.currentTarget;
    const rect = th?.getBoundingClientRect?.();
    if (!rect) return false;
    return rect.right - event.clientX <= SESSION_RESIZE_HANDLE_W;
  };

  const getSessionResizeTarget = (event) => {
    const row = sessionHeaderRowRef.current;
    if (!row) return null;
    const headers = Array.from(row.querySelectorAll('th[data-colkey]'));
    for (const th of headers) {
      const key = th.dataset.colkey;
      if (!key) continue;
      const rect = th.getBoundingClientRect();
      if (Math.abs(event.clientX - rect.right) <= SESSION_RESIZE_HANDLE_W / 2) {
        return { key, th };
      }
    }
    return null;
  };

  const handleSessionHeaderMouseMove = () => (event) => {
    const target = getSessionResizeTarget(event);
    clearSessionResizeHover();
    if (target?.th) {
      target.th.classList.add('server-status-resize-active');
    }
  };

  const handleSessionHeaderMouseLeave = () => () => {
    clearSessionResizeHover();
  };

  const sessionHeaderStyle = {
    position: 'relative',
    cursor: 'ew-resize',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  const renderSessionHeader = (label, field, sortable = true) => (
    <th
      className={`server-status-header-cell${sortable ? ' server-status-sortable' : ''}`}
      data-colkey={field}
      style={sortable ? sessionHeaderStyle : { position: 'relative', whiteSpace: 'nowrap' }}
      onMouseDown={(e) => {
        if (!isInSessionResizeLane(e)) return;
        startSessionResize(field, e);
      }}
      onClick={sortable ? (e) => {
        if (sessionSkipClickRef.current || isInSessionResizeLane(e)) {
          sessionSkipClickRef.current = false;
          return;
        }
        toggleSessionSort(field);
      } : undefined}
    >
      <span className="server-status-header-label">
        {sortable ? `${label} ${renderSortIndicator(field)}` : label}
      </span>
      <span
        className="server-status-resize-handle"
        onMouseDown={(e) => startSessionResize(field, e)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
    </th>
  );

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ marginBottom: 12 }}>Server Status</h2>
      {loading && <div>Loading…</div>}

      {health && (
        <div style={{ marginBottom: 16 }}>
          <h3>Health</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(200px, 1fr))', gap: 8 }}>
            <div>Uptime: {fmtUptime(health.uptimeSec)}</div>
            <div>Node: {health.node}</div>
            <div>RSS: {Math.round((health.rss || 0)/1024/1024)} MB</div>
            <div>Heap: {Math.round((health.heapUsed || 0)/1024/1024)}/{Math.round((health.heapTotal || 0)/1024/1024)} MB</div>
            <div>Load: {health.load1?.toFixed?.(2)} / {health.load5?.toFixed?.(2)} / {health.load15?.toFixed?.(2)}</div>
            <div>CPUs: {health.cpus}</div>
          </div>
        </div>
      )}

      {stats && (
        <div style={{ marginBottom: 16 }}>
          <h3>Sessions</h3>
          <div>Active Users: {stats.activeUsers} | Active Sessions: {stats.activeSessions} | Touches last 5m: {stats.touches5m}</div>
        </div>
      )}

      <div>
        <h3>Active Sessions</h3>
        <div style={{ overflow: 'auto' }}>
          <table className="server-status-table" style={{ width: 'max-content', minWidth: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: sessionColWidths.user }} />
              <col style={{ width: sessionColWidths.login }} />
              <col style={{ width: sessionColWidths.type }} />
              <col style={{ width: sessionColWidths.client }} />
              <col style={{ width: sessionColWidths.ip }} />
              <col style={{ width: sessionColWidths.useragent }} />
              <col style={{ width: sessionColWidths.issued_at }} />
              <col style={{ width: sessionColWidths.last_seen_at }} />
              <col style={{ width: sessionColWidths.expires_at }} />
              <col style={{ width: sessionColWidths.requests }} />
              <col style={{ width: sessionColWidths.actions }} />
            </colgroup>
            <thead>
              <tr
                ref={sessionHeaderRowRef}
                onMouseMove={handleSessionHeaderMouseMove()}
                onMouseLeave={handleSessionHeaderMouseLeave()}
                onMouseDown={(e) => {
                  const target = getSessionResizeTarget(e);
                  if (!target) return;
                  startSessionResize(target.key, e, target.th);
                }}
              >
                {renderSessionHeader('User', 'user')}
                {renderSessionHeader('Login', 'login')}
                {renderSessionHeader('Type', 'type')}
                {renderSessionHeader('Client', 'client')}
                {renderSessionHeader('IP', 'ip')}
                {renderSessionHeader('User Agent', 'useragent')}
                {renderSessionHeader('Issued', 'issued_at')}
                {renderSessionHeader('Last Seen', 'last_seen_at')}
                {renderSessionHeader('Expires', 'expires_at')}
                {renderSessionHeader('Requests', 'requests')}
                {renderSessionHeader('Actions', 'actions', false)}
              </tr>
            </thead>
            <tbody>
                {sortedSessions.length ? sortedSessions.map(r => (
                  <tr key={r.jti}>
                  <td>{r.userFullName || r.login || r.userid}</td>
                  <td>{r.login}</td>
                  <td>{(r.isclient === true || r.isclient === 1 || String(r.isclient).toLowerCase() === 'true' || String(r.isclient) === '1') ? 'Client' : 'Internal'}</td>
                  <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.clientnames || ''}</td>
                  <td>{r.ip}</td>
                  <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.useragent}</td>
                  <td>{fmt(r.issued_at)}</td>
                  <td>{fmt(r.last_seen_at)}</td>
                  <td>{fmt(r.expires_at)}</td>
                  <td>{r.requests}</td>
                  <td><button className="btn" onClick={() => invalidate(r.jti)}>Force Logout</button></td>
                </tr>
              )) : (
                <tr><td colSpan={11}>No active sessions.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
